import { useAuth } from '@clerk/react'
import type { ReactNode } from 'react'
import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import type { TimerStatePayload } from '../../db/schema/sessions.js'
import {
  AUTO_SAVE_DEBOUNCE_MS,
  AUTO_SAVE_FEEDBACK_MS,
  type AutoSaveState,
} from '../../hooks/use-auto-save.js'
import type { LandingMode } from '../../lib/session-rules.js'
import { trpc } from '../../lib/trpc.js'

// =============================================================================
// Types
// =============================================================================

export interface EditorSessionDraft {
  code: string
  updatedAt: string
}

/**
 * The editor's client-side view of a problem session. Distinct from the
 * Drizzle row types in db/schema/sessions.ts (hence the Editor prefix).
 */
export interface EditorSession {
  id: string
  startedAt: string
  drafts: Record<string, EditorSessionDraft>
}

/**
 * Session fetch lifecycle. `resolving` is a first-class state — it replaces
 * the old `sessionDataApplied` gate boolean that disambiguated "query not yet
 * applied" from "resolved with no session".
 */
export type SessionResolution =
  | { status: 'resolving' }
  | { status: 'ready'; session: EditorSession }
  | { status: 'none' }
  | { status: 'error'; message: string }

/**
 * Save status for the UI indicator. The shared auto-save vocabulary plus
 * 'restored', which only this provider has (code loaded from a past session).
 */
export type SaveStatus = AutoSaveState | 'restored'

/**
 * Outcome of a draft persist. Returned rather than thrown: the debounced save,
 * the createSession flush, and Run/Submit all call into this path without a
 * catch, and a rejection there would either go unhandled or abort an execution
 * over a background save. Callers that gate UI on the draft actually reaching
 * the server (applyCode) narrow on `ok`; the rest ignore it as before.
 */
export type DraftSaveResult =
  | { ok: true }
  | { ok: false; reason: 'no-session' | 'draft-save-failed' }

interface SessionContextValue {
  /** Current active session (null if no session yet) */
  session: EditorSession | null
  /** Session fetch lifecycle as a tagged union — prefer narrowing on this over loading/session null-checks */
  resolution: SessionResolution
  /** Whether a new session should be started (previous session timed out) */
  shouldStartNew: boolean
  /** Whether session data is loading */
  loading: boolean
  /** Error if session fetch failed */
  error: string | null
  /** Create a new session */
  createSession: () => Promise<EditorSession | null>
  /** Save draft for a language (creates session if needed) */
  saveDraft: (language: string, code: string) => Promise<void>
  /** Save draft immediately, bypassing debounce (for Run/Submit). Resolves once the save settles, reporting whether it reached the server. */
  saveDraftImmediate: (
    language: string,
    code: string,
  ) => Promise<DraftSaveResult>
  /** Cancel any pending save operation */
  cancelPendingSave: () => void
  /** Apply a system-generated edit (e.g. formatter output) without triggering the user-edit pipeline */
  applySystemEdit: (language: string, code: string) => Promise<void>
  /** Get draft code for a language from current session */
  getDraft: (language: string) => string | null
  /** Current save status for UI indicator */
  saveStatus: SaveStatus
  /** Set save status to 'restored' when loading code from session */
  setRestoredStatus: () => void
  /** Timer state from the current session (persisted server-side) */
  timerState: TimerStatePayload | null
  /** Timestamp of last session update (ISO string) */
  lastKnownTimestamp: string | null
  /** Close the current session (set session to null locally) */
  closeSession: () => void
  /** True when last activity is in the [2h, 24h) band — show Continue/Start Fresh modal */
  needsContinueDecision: boolean
  /** Set when the server auto-expired the previous session AND the user hasn't suppressed the notice */
  expiredNotice: { previousSessionId: string } | null
  /** Dismiss the Continue/Start Fresh modal as "Continue" — keeps the existing session */
  acknowledgeContinueDecision: () => void
  /** Dismiss the >24h expired-session notification */
  acknowledgeExpiredNotice: () => void
  /** Explicit Start Fresh — closes the current session server-side and refetches */
  startFreshFromCurrent: () => Promise<void>
  /**
   * Counter that increments whenever the user explicitly restores code from
   * a previous session. The Monaco editor hook depends on this so that an
   * external code overwrite (vs. a normal user edit) re-triggers the
   * load-draft effect.
   */
  restoreSeq: number
  /**
   * Called by the Restore flow after the new code has been saved to the
   * current sessionDraft. Bumps `restoreSeq` so the editor reloads.
   */
  notifyRestoreCompleted: () => void
  /**
   * Register the editor's reset callback so consumers above the
   * EditorActionsProvider (e.g. SessionLifecycleModals) can request a reset
   * without depending on the actions-context hierarchy.
   */
  registerEditorResetCallback: (callback: (() => void) | null) => void
  /**
   * Register the editor's code replacement callback so SessionProvider can
   * preload closed-session code even though CodeEditorPane owns the real editor
   * action below the provider boundary.
   */
  registerEditorSetCodeCallback: (
    callback: ((code: string) => void) | null,
  ) => void
  /**
   * Trigger the registered editor reset callback. Used by Start Fresh flows
   * in SessionLifecycleModals to clear the editor back to the starter stub.
   */
  requestEditorReset: () => void
  /** The landing mode determined by the server for the current problem visit */
  landingMode: LandingMode['mode'] | null
  /** Code to preload into the editor (from closed sessions or existing drafts) */
  preloadedCode: string | null
  /** Language of the preloaded code */
  preloadedLanguage: string | null
  /** Elapsed timer ms from the server (0 for closed-* modes; session total for open-* modes) */
  preloadedTimerAggregateMs: number
}

// =============================================================================
// Constants
// =============================================================================

// Code drafts are the largest payload the app auto-saves, so they take the
// slowest tier of the shared debounce policy. Both numbers live in
// hooks/use-auto-save.ts so every auto-saving surface agrees on them.
const DRAFT_SAVE_DEBOUNCE_MS = AUTO_SAVE_DEBOUNCE_MS.slow
const SAVED_STATUS_DURATION_MS = AUTO_SAVE_FEEDBACK_MS

// =============================================================================
// Context
// =============================================================================

const SessionContext = createContext<SessionContextValue | null>(null)

export function useSession(): SessionContextValue {
  const context = use(SessionContext)
  if (!context) {
    throw new Error('useSession must be used within a SessionProvider')
  }
  return context
}

export function useSessionOptional(): SessionContextValue | null {
  return use(SessionContext)
}

// =============================================================================
// Provider
// =============================================================================

interface SessionProviderProps {
  problemId: string | null
  children: ReactNode
}

export function SessionProvider({ problemId, children }: SessionProviderProps) {
  const { isSignedIn } = useAuth()
  // Local session state (kept in sync with tRPC query data)
  // Session fetch lifecycle (kept in sync with tRPC query data by the sync
  // effect below). 'resolving' covers both the query being in flight and the
  // one-render gap between query resolution and the sync effect applying it.
  const [resolution, setResolution] = useState<SessionResolution>({
    status: 'resolving',
  })
  const [shouldStartNew, setShouldStartNew] = useState(false)
  const [timerState, setTimerState] = useState<TimerStatePayload | null>(null)
  const [lastKnownTimestamp, setLastKnownTimestamp] = useState<string | null>(
    null,
  )
  const [needsContinueDecision, setNeedsContinueDecision] = useState(false)
  const [expiredNotice, setExpiredNotice] = useState<{
    previousSessionId: string
  } | null>(null)
  const [restoreSeq, setRestoreSeq] = useState(0)
  const [landingMode, setLandingMode] = useState<LandingMode['mode'] | null>(
    null,
  )
  const [preloadedCode, setPreloadedCode] = useState<string | null>(null)
  const [preloadedLanguage, setPreloadedLanguage] = useState<string | null>(
    null,
  )
  const [preloadedTimerAggregateMs, setPreloadedTimerAggregateMs] =
    useState<number>(0)
  const [editorSetCodeRegistrationSeq, setEditorSetCodeRegistrationSeq] =
    useState(0)

  // Ref tracking which (landingMode, sourceSessionId) preload has been applied
  // to the editor. Prevents re-applying the same preload on subsequent renders.
  const appliedPreloadForSessionId = useRef<string | null>(null)

  // Edge-trigger guard for `needsContinueDecision`. Holds the identity of the
  // landing that last raised the modal so a refetch of the same landing (e.g.
  // window focus revalidation) doesn't re-open a user-dismissed modal.
  const decisionAppliedForKey = useRef<string | null>(null)

  // Edge-trigger guard for auto-expire close. Holds the previousSessionId we
  // last fired closeExpired for, so a refetch of the same open-expired-stub
  // response doesn't re-fire the mutation.
  const closeExpiredFiredForKey = useRef<string | null>(null)

  const isCreatingSessionRef = useRef(false)

  // Save status tracking
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle')
  const savedStatusTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  )

  // Debounce ref for draft saving
  const saveDraftTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pendingDraftRef = useRef<{ language: string; code: string } | null>(
    null,
  )
  const isFlushingPendingRef = useRef(false)
  // Ref to access latest session in debounced callbacks (avoids stale closures)
  // Single derived view of the resolved session (from the resolution union).
  // Used by render-time reads and callback deps; sessionRef mirrors it for
  // stale-closure-safe access inside debounced callbacks.
  const session = resolution.status === 'ready' ? resolution.session : null
  const sessionRef = useRef<EditorSession | null>(null)
  sessionRef.current = session

  // Stable ref for problemId so saveDraftImmediate can always read the latest
  const problemIdRef = useRef(problemId)
  problemIdRef.current = problemId

  // =========================================================================
  // tRPC: Fetch current session
  // =========================================================================

  const queryEnabled = !!problemId && !!isSignedIn
  const currentSessionQuery = trpc.sessions.getCurrent.useQuery(
    { problemId: problemId! },
    { enabled: queryEnabled },
  )

  // Reset state when problem changes
  // IMPORTANT: This effect MUST be declared before the sync effect below.
  // React runs effects in declaration order. When problemId changes, both
  // effects fire. If sync ran first it would set session from cache, then
  // reset would null it out. With reset first, the session is cleared and
  // then immediately repopulated from cached query data.
  // biome-ignore lint/correctness/useExhaustiveDependencies: problemId triggers reset when problem changes
  useEffect(() => {
    // Cancel any pending draft save to prevent stale data from being saved
    // to the wrong problem's session after switching problems
    const pendingBeforeCancel = pendingDraftRef.current
    if (saveDraftTimeoutRef.current) {
      clearTimeout(saveDraftTimeoutRef.current)
      saveDraftTimeoutRef.current = null
    }
    if (pendingBeforeCancel) {
      // Capture old-session pending draft and flush immediately before state reset.
      // This prevents quick navigation from dropping the last unsaved draft.
      const flushDraft = pendingBeforeCancel
      const flushSessionId = sessionRef.current?.id ?? null
      pendingDraftRef.current = null
      void flushPendingDraftBeforeReset(flushDraft, flushSessionId)
    } else {
      pendingDraftRef.current = null
    }

    setResolution({ status: 'resolving' })
    setShouldStartNew(false)
    setTimerState(null)
    setLastKnownTimestamp(null)
    setNeedsContinueDecision(false)
    setExpiredNotice(null)
    setSaveStatus('idle')
    setLandingMode(null)
    setPreloadedCode(null)
    setPreloadedLanguage(null)
    setPreloadedTimerAggregateMs(0)
    appliedPreloadForSessionId.current = null
    decisionAppliedForKey.current = null
    closeExpiredFiredForKey.current = null
    isCreatingSessionRef.current = false
    if (savedStatusTimeoutRef.current) {
      clearTimeout(savedStatusTimeoutRef.current)
    }
  }, [problemId])

  // =========================================================================
  // tRPC: Mutations (declared before sync effect because the effect references
  // them; others used by callbacks below are declared further down)
  // =========================================================================

  const trpcUtils = trpc.useUtils()
  const closeExpiredMutation = trpc.sessions.closeExpired.useMutation()

  // Sync tRPC query data into local state, switching on result.mode
  // biome-ignore lint/correctness/useExhaustiveDependencies: we only want to run when query data, error, or problemId changes
  useEffect(() => {
    const result = currentSessionQuery.data
    if (!result) {
      if (currentSessionQuery.error) {
        // Query resolved with an error — `data` will stay undefined, so the
        // success branch below won't fire. Settle the resolution to error so
        // `loading` can drop and consumers (load-draft, save effect, timer)
        // stop bailing. The `error` field already surfaces the failure to the UI.
        setResolution({ status: 'error', message: 'Failed to load session' })
      }
      return
    }

    setLandingMode(result.mode)

    switch (result.mode) {
      case 'fresh-stub':
      case 'closed-stale-stub': {
        // No prior session or session too old — treat as fresh
        setResolution({ status: 'none' })
        setShouldStartNew(result.shouldStartNew)
        setTimerState(null)
        setLastKnownTimestamp(null)
        setPreloadedCode(null)
        setPreloadedLanguage(null)
        setPreloadedTimerAggregateMs(0)
        setNeedsContinueDecision(false)
        setExpiredNotice(null)
        break
      }

      case 'closed-recent-silent': {
        // Session closed <2h ago — silently preload code, no modal
        setResolution({ status: 'none' })
        setShouldStartNew(result.shouldStartNew)
        setTimerState(null)
        setLastKnownTimestamp(null)
        setPreloadedCode(result.preload?.code ?? null)
        setPreloadedLanguage(result.preload?.language ?? null)
        setPreloadedTimerAggregateMs(0)
        setNeedsContinueDecision(false)
        setExpiredNotice(null)
        break
      }

      case 'closed-needs-decision': {
        // Session closed 2-24h ago — preload code AND show Continue/Fresh modal
        setResolution({ status: 'none' })
        setShouldStartNew(result.shouldStartNew)
        setTimerState(null)
        setLastKnownTimestamp(null)
        setPreloadedCode(result.preload?.code ?? null)
        setPreloadedLanguage(result.preload?.language ?? null)
        setPreloadedTimerAggregateMs(0)
        const key = `closed:${result.preload?.sourceSessionId ?? 'unknown'}`
        if (decisionAppliedForKey.current !== key) {
          decisionAppliedForKey.current = key
          setNeedsContinueDecision(true)
        }
        setExpiredNotice(null)
        break
      }

      case 'open-silent':
      case 'open-needs-decision': {
        // Session is open — restore existing session and populate preload from drafts
        const querySession = result.session
        if (querySession || !sessionRef.current) {
          setResolution(
            querySession
              ? { status: 'ready', session: querySession }
              : { status: 'none' },
          )
          setShouldStartNew(result.shouldStartNew)
          setTimerState(querySession?.timerState ?? null)
          setLastKnownTimestamp(querySession?.lastKnownTimestamp ?? null)
        }
        setPreloadedTimerAggregateMs(result.timerAggregateMs)
        if (result.mode === 'open-needs-decision') {
          const key = `open:${result.session?.id ?? 'unknown'}`
          if (decisionAppliedForKey.current !== key) {
            decisionAppliedForKey.current = key
            setNeedsContinueDecision(true)
          }
        } else {
          decisionAppliedForKey.current = null
          setNeedsContinueDecision(false)
        }
        setExpiredNotice(null)
        // For open sessions, preloadedCode is set to null here — the editor
        // loads its content from getDraft() via the existing load-draft effect.
        // We do not set preloadedCode/preloadedLanguage here to avoid double-loading.
        setPreloadedCode(null)
        setPreloadedLanguage(null)
        break
      }

      case 'open-expired-stub': {
        // Session is past auto-expire age. The server returns this mode as a
        // pure read (no DB write). We fire sessions.closeExpired to perform
        // the server-side close + draft snapshot, then invalidate getHistory
        // so the Attempts tab refetches with the snapshot row included.
        setResolution({ status: 'none' })
        setShouldStartNew(result.shouldStartNew)
        setTimerState(null)
        setLastKnownTimestamp(null)
        setPreloadedCode(null)
        setPreloadedLanguage(null)
        setPreloadedTimerAggregateMs(0)
        setNeedsContinueDecision(false)
        const expired = result.expiredPreviousSession
        if (expired && !expired.suppressNotice) {
          setExpiredNotice({ previousSessionId: expired.previousSessionId })
        } else {
          setExpiredNotice(null)
        }

        const previousSessionId = expired?.previousSessionId
        if (
          previousSessionId &&
          closeExpiredFiredForKey.current !== previousSessionId
        ) {
          closeExpiredFiredForKey.current = previousSessionId
          const currentProblemId = problemId
          void closeExpiredMutation
            .mutateAsync({ sessionId: previousSessionId })
            .then(async () => {
              if (!currentProblemId) return
              // Only invalidate getHistory (Attempts tab data). DO NOT
              // invalidate getCurrent: doing so triggers an immediate
              // refetch that returns closed-stale-stub (the session is
              // now closed), which causes the sync effect to flip out of
              // open-expired-stub and clear expiredNotice — yanking the
              // expired modal out from under the user mid-read. The
              // cached open-expired-stub stays put until the user
              // dismisses the modal or unmounts the provider.
              await trpcUtils.sessions.getHistory.invalidate({
                problemId: currentProblemId,
              })
            })
            .catch((err) => {
              console.error('closeExpired failed:', err)
              // Self-heals on next problem mount: the guard ref clears on
              // problem change, and a subsequent getCurrent will trigger
              // this branch again.
            })
        }
        break
      }

      default: {
        // Exhaustiveness guard: adding a LandingMode makes `result` fail to
        // narrow to `never` here, a compile error. Throw with only `mode` —
        // the full payload can carry student draft code (preload.code), which
        // must not reach error logs.
        const exhaustive: never = result
        throw new Error(
          `Unexpected LandingMode: ${(exhaustive as { mode: string }).mode}`,
        )
      }
    }
  }, [currentSessionQuery.data, currentSessionQuery.error, problemId])

  // Apply preloaded code to the editor exactly once per (landingMode, sourceSessionId).
  // Only applies for closed-* modes where preloadedCode is set; open-* sessions
  // continue to use the existing getDraft() / load-draft effect path.
  useEffect(() => {
    if (!preloadedCode || !landingMode) return
    if (
      landingMode !== 'closed-recent-silent' &&
      landingMode !== 'closed-needs-decision'
    )
      return

    // Derive a stable "source session id" from the query data to use as guard key.
    // For closed-* modes the server response includes sourceSessionId on the mode.
    const result = currentSessionQuery.data
    if (!result) return
    // The closed-* landing modes carry sourceSessionId in result.preload.
    const sourceId = result.preload?.sourceSessionId ?? null
    if (!sourceId) return

    // Guard: only apply once per sourceSessionId
    if (appliedPreloadForSessionId.current === sourceId) {
      return
    }

    const setEditorCode = editorSetCodeCallbackRef.current
    if (!setEditorCode) {
      return
    }

    appliedPreloadForSessionId.current = sourceId
    setEditorCode(preloadedCode)
  }, [
    preloadedCode,
    landingMode,
    currentSessionQuery.data,
    editorSetCodeRegistrationSeq,
  ])

  // =========================================================================
  // tRPC: Mutations (used by callbacks below)
  // =========================================================================

  const createSessionMutation = trpc.sessions.create.useMutation()
  const saveDraftMutation = trpc.sessions.saveDraft.useMutation()
  const closeSessionMutation = trpc.sessions.closeSession.useMutation()

  async function flushPendingDraftBeforeReset(
    pending: { language: string; code: string },
    sessionId: string | null,
  ): Promise<void> {
    if (isFlushingPendingRef.current) return
    isFlushingPendingRef.current = true
    try {
      if (!sessionId) {
        return
      }

      await saveDraftMutation.mutateAsync({
        id: sessionId,
        language: pending.language,
        code: pending.code,
      })
    } catch {
      // Best-effort flush before reset; failure should not block navigation.
    } finally {
      isFlushingPendingRef.current = false
    }
  }

  // Stable ref for the mutation to avoid re-creating createSession on mutation
  // state changes (idle → pending → success), which would re-trigger effects
  // that depend on createSession and cause a session-creation loop.
  const createSessionMutationRef = useRef(createSessionMutation)
  createSessionMutationRef.current = createSessionMutation

  // Derived from the resolution union. 'resolving' holds through the gap
  // between query resolution and the sync effect applying data, so consumers
  // (e.g. load-draft) never observe loading=false with an unresolved session.
  // The isLoading term covers re-enable transitions (sign-in) where a stale
  // resolution value could otherwise report done during the first fetch.
  const loading = queryEnabled
    ? currentSessionQuery.isLoading ||
      (resolution.status === 'resolving' && !currentSessionQuery.error)
    : false
  const error =
    resolution.status === 'error' || currentSessionQuery.error
      ? 'Failed to load session'
      : null

  // Create a new session
  const createSession = useCallback(async (): Promise<EditorSession | null> => {
    if (!problemId || !isSignedIn) return null
    if (isCreatingSessionRef.current) return null

    isCreatingSessionRef.current = true
    try {
      const data = await createSessionMutationRef.current.mutateAsync({
        problemId,
      })
      const newSession: EditorSession = {
        id: data.session.id,
        startedAt: data.session.startedAt,
        drafts: {},
      }

      setResolution({ status: 'ready', session: newSession })
      sessionRef.current = newSession // Update ref immediately for synchronous access
      setShouldStartNew(false)

      // Sync React Query cache so remount has fresh data
      trpcUtils.sessions.getCurrent.setData(
        { problemId: problemId! },
        {
          mode: 'open-silent',
          preload: null,
          timerAggregateMs: 0,
          session: {
            ...newSession,
            timerState: null,
            lastKnownTimestamp: new Date().toISOString(),
          },
          shouldStartNew: false,
          needsContinueDecision: false,
          expiredPreviousSession: null,
        },
      )
      // Invalidate history so the Attempts tab picks up the new session
      trpcUtils.sessions.getHistory.invalidate()

      // Flush any pending draft immediately. When createSession is called
      // from the Run/Submit handler (before the debounce fires), the
      // sessionId change triggers the draft loading effect which calls
      // cancelPendingSave() — killing the debounced save. By flushing
      // here, the draft is saved before cancelPendingSave can cancel it.
      const pending = pendingDraftRef.current
      if (pending) {
        // Clear the debounce timer since we're saving now
        if (saveDraftTimeoutRef.current) {
          clearTimeout(saveDraftTimeoutRef.current)
          saveDraftTimeoutRef.current = null
        }
        pendingDraftRef.current = null
        // Fire-and-forget — don't await to avoid blocking session creation return
        saveDraftToServerRef.current(
          newSession.id,
          pending.language,
          pending.code,
          problemId!,
        )
      }

      return newSession
    } catch (err) {
      console.error('Error creating session:', err)
      return null
    } finally {
      isCreatingSessionRef.current = false
    }
  }, [
    problemId,
    isSignedIn,
    trpcUtils.sessions.getCurrent.setData,
    trpcUtils.sessions.getHistory.invalidate,
  ])

  // Internal function to actually save draft to server
  const saveDraftToServer = useCallback(
    async (
      sessionId: string,
      language: string,
      code: string,
      forProblemId: string,
    ): Promise<DraftSaveResult> => {
      // Clear any existing "saved" timeout
      if (savedStatusTimeoutRef.current) {
        clearTimeout(savedStatusTimeoutRef.current)
      }

      setSaveStatus('saving')

      // The network write alone decides ok/failure. Everything after it is
      // local reconciliation over a draft the server already holds, so it gets
      // its own guard below rather than sharing this outcome.
      const written = await saveDraftMutation
        .mutateAsync({ id: sessionId, language, code })
        .then((data) => ({ ok: true as const, data }))
        .catch((err: unknown) => ({ ok: false as const, err }))

      if (!written.ok) {
        console.error('Error saving draft:', written.err)
        // Surface the failure instead of collapsing to 'idle', which the footer
        // renders identically to a successful save ("Saved", green) — a false
        // all-clear over code the server never received. Editing again retries.
        setSaveStatus('error')
        // The footer indicator alone is not enough: it is not tied to the action
        // the user just took, so report the failure to the caller as well.
        return { ok: false, reason: 'draft-save-failed' }
      }
      const { data } = written

      // The server has the draft from here on, so `ok` is already decided. This
      // guard is not about the outcome — it keeps saveDraftToServer's no-throw
      // contract, which the debounced setTimeout callback, the fire-and-forget
      // createSession flush, and both Run/Submit handlers all rely on.
      try {
        // Update local session state with new draft
        setResolution((prev) => {
          if (prev.status !== 'ready' || prev.session.id !== sessionId)
            return prev
          return {
            status: 'ready',
            session: {
              ...prev.session,
              drafts: {
                ...prev.session.drafts,
                [language]: { code, updatedAt: data.draft.updatedAt },
              },
            },
          }
        })

        // Update React Query cache with new draft data
        if (forProblemId) {
          trpcUtils.sessions.getCurrent.setData(
            { problemId: forProblemId },
            (old) => {
              if (!old?.session || old.session.id !== sessionId) return old
              return {
                ...old,
                // Typing bumps server-side lastActivityAt, so the next
                // getCurrent classifies as <2h. Clear the lifecycle flags
                // here so the sync useEffect can't re-open a dismissed modal.
                needsContinueDecision: false,
                expiredPreviousSession: null,
                session: {
                  ...old.session,
                  drafts: {
                    ...old.session.drafts,
                    [language]: {
                      code,
                      updatedAt: data.draft.updatedAt,
                    },
                  },
                },
              }
            },
          )
        }

        // Show "saved" status briefly, then return to idle
        setSaveStatus('saved')
        savedStatusTimeoutRef.current = setTimeout(() => {
          setSaveStatus('idle')
        }, SAVED_STATUS_DURATION_MS)
      } catch (err) {
        // Reported, never returned as a failed save: the write did land, and
        // telling applyCode otherwise would leave the editor showing
        // pre-restore code the server no longer has.
        console.error('Error reconciling saved draft:', err)
      }

      return { ok: true }
    },
    [saveDraftMutation, trpcUtils.sessions.getCurrent.setData],
  )

  // Stable ref for saveDraftToServer so createSession can flush pending drafts
  // without adding saveDraftToServer to its dependency array.
  const saveDraftToServerRef = useRef(saveDraftToServer)
  saveDraftToServerRef.current = saveDraftToServer

  // Save draft for a language (debounced, creates session if needed)
  const saveDraft = useCallback(
    async (language: string, code: string) => {
      if (!problemId || !isSignedIn) {
        return
      }

      // Check if code is different from what's already saved
      const savedCode = session?.drafts[language]?.code
      if (savedCode === code) {
        // Code hasn't changed from what's saved - no need to save again
        return
      }
      // Store pending draft and show pending status
      pendingDraftRef.current = { language, code }

      // Only show pending if we're not already saving
      setSaveStatus((current) => (current === 'saving' ? 'saving' : 'pending'))

      // Clear existing timeout
      if (saveDraftTimeoutRef.current) {
        clearTimeout(saveDraftTimeoutRef.current)
      }

      // Debounce the actual save
      saveDraftTimeoutRef.current = setTimeout(async () => {
        const pending = pendingDraftRef.current
        if (!pending) return

        // Use ref to get latest session (avoids stale closure)
        let currentSession = sessionRef.current

        // Create session if none exists
        if (!currentSession) {
          currentSession = await createSession()
          // Auto-create may have completed while we awaited; re-read ref before dropping save
          if (!currentSession) {
            currentSession = sessionRef.current
          }
          if (!currentSession) {
            setSaveStatus('idle')
            return
          }
        }

        // Double-check the code is still different (using latest session from ref)
        const currentSavedCode = currentSession.drafts[pending.language]?.code
        if (currentSavedCode === pending.code) {
          // Code matches what's saved - skip the save
          pendingDraftRef.current = null
          setSaveStatus('idle')
          return
        }

        await saveDraftToServer(
          currentSession.id,
          pending.language,
          pending.code,
          problemId!,
        )
        pendingDraftRef.current = null
      }, DRAFT_SAVE_DEBOUNCE_MS)
    },
    [problemId, isSignedIn, session, createSession, saveDraftToServer],
  )

  // Cancel any pending save operation (called when loading a draft to prevent stale saves)
  const cancelPendingSave = useCallback(() => {
    if (saveDraftTimeoutRef.current) {
      clearTimeout(saveDraftTimeoutRef.current)
      saveDraftTimeoutRef.current = null
    }
    pendingDraftRef.current = null
    // Only reset status if we were in pending state
    setSaveStatus((current) => (current === 'pending' ? 'idle' : current))
  }, [])

  // Apply a system-generated edit (e.g. formatter output) without persisting.
  // The formatter is idempotent — it re-applies on every page load — so the
  // session draft does not need to store the formatted version. We update
  // pendingDraftRef so any concurrent getDraft() call sees the latest in-editor
  // code, but we deliberately DO NOT schedule a debounced save and DO NOT
  // auto-create a session. Persisting here would route the formatter through
  // the save pipeline, which on first visit calls createSession and triggers a
  // setSession(newSession, drafts:{}) that races with load-draft and reverts
  // the editor to the unformatted source.
  const applySystemEdit = useCallback(
    async (language: string, code: string) => {
      if (!problemId || !isSignedIn) {
        return
      }
      pendingDraftRef.current = { language, code }
    },
    [problemId, isSignedIn],
  )

  // Save draft immediately, bypassing debounce (used by Run/Submit handlers).
  // Returns a promise that resolves when the server save completes, carrying
  // whether the draft actually reached the server — callers that gate a UI
  // action on the persist (applyCode) must narrow on it, not just await.
  const saveDraftImmediate = useCallback(
    (language: string, code: string): Promise<DraftSaveResult> => {
      const currentSession = sessionRef.current
      // Nothing to save into: the draft does not reach the server, so this is a
      // failure for callers that gate on it, not a silent no-op success.
      if (!currentSession)
        return Promise.resolve({ ok: false, reason: 'no-session' })

      // Cancel any pending debounced save — we're saving now
      if (saveDraftTimeoutRef.current) {
        clearTimeout(saveDraftTimeoutRef.current)
        saveDraftTimeoutRef.current = null
      }
      pendingDraftRef.current = null

      // Skip if code matches what's already saved — the server already has this
      // exact code, so the caller's post-condition is satisfied.
      const currentSavedCode = currentSession.drafts[language]?.code
      if (currentSavedCode === code) return Promise.resolve({ ok: true })

      return saveDraftToServerRef.current(
        currentSession.id,
        language,
        code,
        problemIdRef.current!,
      )
    },
    [],
  )

  // Get draft code for a language from current session
  // Uses sessionRef instead of session state to keep a stable identity —
  // prevents the load-draft effect from re-running after every save
  const getDraft = useCallback((language: string): string | null => {
    // Check pending draft first (most up-to-date, not yet saved)
    if (pendingDraftRef.current?.language === language) {
      return pendingDraftRef.current.code
    }
    // Fall back to saved draft in session
    return sessionRef.current?.drafts[language]?.code ?? null
  }, [])

  // Set status to 'restored' when loading code from session
  const setRestoredStatus = useCallback(() => {
    setSaveStatus('restored')
  }, [])

  // Close the current session (set session to null locally)
  const closeSession = useCallback(() => {
    setResolution({ status: 'none' })
    sessionRef.current = null
    setTimerState(null)
    setLastKnownTimestamp(null)
  }, [])

  // Acknowledge a Continue/Start Fresh modal as "Continue" — keep session as-is.
  const acknowledgeContinueDecision = useCallback(() => {
    setNeedsContinueDecision(false)
  }, [])

  // Dismiss the >24h expired-session notification (modal already handles
  // persisting the suppress preference if checked).
  const acknowledgeExpiredNotice = useCallback(() => {
    setExpiredNotice(null)
  }, [])

  // Bump after the Restore flow completes so the editor's load-draft effect
  // re-fires and picks up the freshly-saved draft code.
  const notifyRestoreCompleted = useCallback(() => {
    setRestoreSeq((n) => n + 1)
  }, [])

  // Bridge for editor reset actions across the EditorActionsProvider boundary.
  // SessionLifecycleModals lives above EditorActionsProvider (see editor.tsx)
  // so its useEditorActions() resolves to the no-op default. CodeEditorPane
  // registers its handleReset here on mount; the modals invoke it via
  // requestEditorReset.
  const editorResetCallbackRef = useRef<(() => void) | null>(null)
  const editorSetCodeCallbackRef = useRef<((code: string) => void) | null>(null)
  const registerEditorResetCallback = useCallback(
    (callback: (() => void) | null) => {
      editorResetCallbackRef.current = callback
    },
    [],
  )
  const registerEditorSetCodeCallback = useCallback(
    (callback: ((code: string) => void) | null) => {
      editorSetCodeCallbackRef.current = callback
      setEditorSetCodeRegistrationSeq((n) => n + 1)
    },
    [],
  )
  const requestEditorReset = useCallback(() => {
    editorResetCallbackRef.current?.()
  }, [])

  // "Start Fresh" — close the current session server-side, then eagerly
  // create the new one so it appears as a stub in Attempts immediately
  // (rather than only after the first run/submit lazily creates it).
  const startFreshFromCurrent = useCallback(async () => {
    const current = sessionRef.current
    if (!current || !problemId) {
      setNeedsContinueDecision(false)
      return
    }
    try {
      await closeSessionMutation.mutateAsync({ sessionId: current.id })
    } catch (err) {
      console.error('Error closing session for Start Fresh:', err)
    }
    setNeedsContinueDecision(false)
    setResolution({ status: 'none' })
    sessionRef.current = null
    setTimerState(null)
    setLastKnownTimestamp(null)
    setShouldStartNew(true)

    // Eagerly create the new session so the Attempts tab gets a stub for it
    // and the editor has a session to save drafts into without waiting for
    // the first keystroke to trigger lazy creation.
    try {
      await createSession()
    } catch (err) {
      console.error('Error creating new session for Start Fresh:', err)
    }

    await trpcUtils.sessions.getCurrent.invalidate({ problemId })
    await trpcUtils.sessions.getHistory.invalidate({ problemId })
  }, [
    closeSessionMutation,
    createSession,
    problemId,
    trpcUtils.sessions.getCurrent,
    trpcUtils.sessions.getHistory,
  ])

  // Flush pending draft and cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      // Flush any pending draft save before unmounting
      const pending = pendingDraftRef.current
      if (pending && saveDraftTimeoutRef.current) {
        clearTimeout(saveDraftTimeoutRef.current)
        saveDraftTimeoutRef.current = null
        pendingDraftRef.current = null
        const currentSession = sessionRef.current
        if (currentSession) {
          const currentSavedCode = currentSession.drafts[pending.language]?.code
          if (currentSavedCode !== pending.code) {
            saveDraftMutation.mutate({
              id: currentSession.id,
              language: pending.language,
              code: pending.code,
            })
          }
        }
      }
      if (savedStatusTimeoutRef.current) {
        clearTimeout(savedStatusTimeoutRef.current)
      }
    }
  }, [saveDraftMutation.mutate])

  const value = useMemo<SessionContextValue>(() => {
    // Surface a resolution consistent with the derived loading/error the rest
    // of the API exposes: disabled query → none; error/loading override the
    // stored resolution so consumers never see a stale 'ready' during a
    // re-fetch or a 'resolving' after the query has settled.
    const exposedResolution: SessionResolution = !queryEnabled
      ? { status: 'none' }
      : error
        ? { status: 'error', message: error }
        : loading
          ? { status: 'resolving' }
          : resolution
    return {
      session:
        exposedResolution.status === 'ready' ? exposedResolution.session : null,
      resolution: exposedResolution,
      shouldStartNew,
      loading,
      error,
      createSession,
      saveDraft,
      saveDraftImmediate,
      cancelPendingSave,
      applySystemEdit,
      getDraft,
      saveStatus,
      setRestoredStatus,
      timerState,
      lastKnownTimestamp,
      closeSession,
      needsContinueDecision,
      expiredNotice,
      acknowledgeContinueDecision,
      acknowledgeExpiredNotice,
      startFreshFromCurrent,
      restoreSeq,
      notifyRestoreCompleted,
      registerEditorResetCallback,
      registerEditorSetCodeCallback,
      requestEditorReset,
      landingMode,
      preloadedCode,
      preloadedLanguage,
      preloadedTimerAggregateMs,
    }
  }, [
    resolution,
    queryEnabled,
    shouldStartNew,
    loading,
    error,
    createSession,
    saveDraft,
    saveDraftImmediate,
    cancelPendingSave,
    applySystemEdit,
    getDraft,
    setRestoredStatus,
    saveStatus,
    timerState,
    lastKnownTimestamp,
    closeSession,
    needsContinueDecision,
    expiredNotice,
    acknowledgeContinueDecision,
    acknowledgeExpiredNotice,
    startFreshFromCurrent,
    restoreSeq,
    notifyRestoreCompleted,
    registerEditorResetCallback,
    registerEditorSetCodeCallback,
    requestEditorReset,
    landingMode,
    preloadedCode,
    preloadedLanguage,
    preloadedTimerAggregateMs,
  ])

  return <SessionContext value={value}>{children}</SessionContext>
}
